import React, { useEffect, useState } from 'react';
import { Shelf, Text, Icon, Button, AnchorTextLink, Position, Tooltip } from '@chaser/components';
import style from './VoiceCallingBanner.module.scss';
import useDialNumber from '../../hooks/useDialNumber';
import useCustomer from '../../hooks/useCustomer';
import { roles, TaskTypes } from '../../util/constants';
import emitter from '../../util/emitter';
import useOrganisation from '../../hooks/useOrganisation';
import useEvent from '../../hooks/useEvent';
import useShortCode from '../../hooks/useShortCode';
import RoleGate from '../FeatureGate/RoleGate';

const StartCallBanner = ({ open, close, phoneNumber, contactId }) => {
  const shortCode = useShortCode();
  const [numberToDial, setNumberToDial] = useState(phoneNumber);
  const [loading, setLoading] = useState(false);
  const { data: customer } = useCustomer(contactId, shortCode);
  const { CountryCode: countryCode } = useOrganisation();

  const dialNumber = useDialNumber();

  function cancelCall() {
    setLoading(false);
    close();
    emitter.emit('cancelCall');
  }

  useEvent('acceptCall', () => setLoading(false));

  useEffect(() => {
    if (!loading && numberToDial) {
      setNumberToDial(phoneNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, phoneNumber]);

  if (!open) return null;

  const { Name: customerName, FirstName, LastName, tasks } = customer;
  const primaryContact = `${FirstName || 'unknown'} ${LastName || ''}`;

  const task = tasks?.nodes && tasks.nodes.find(f => f.type === TaskTypes.CALL_REMINDER);

  async function handleCall() {
    if (loading) return;

    window.pendo &&
      window.pendo.track('make_phone_call', {
        phoneNumber: numberToDial,
      });

    setLoading(true);
    await dialNumber(numberToDial, {
      primaryContact,
      customerName,
      countryCode,
      contactId,
      shortCode,
      task,
    });
  }

  return (
    <Shelf className={style.notificationBoxWrapper}>
      <Shelf className={style.notificationBox}>
        <Shelf justifyContent="space-between" width="100%" px="small">
          <Shelf className={style.columns__left}>
            <Icon name="phone" />
            <Text color="White" fontWeight="bold">
              Start a call
            </Text>
            <Text color="White" fontWeight="bold">
              Call number: {numberToDial}
            </Text>
          </Shelf>
          <Shelf className={style.columns__right}>
            <AnchorTextLink
              trim={false}
              href="https://help.chaserhq.com/chaser-pricing-details"
              target="_blank"
              color="White"
            >
              <Text fontSize="xsmall" lineHeight={2.5}>
                <u>Additional charges apply</u>
              </Text>
            </AnchorTextLink>
            <AnchorTextLink onClick={cancelCall} color="White" fontWeight="bold">
              Cancel
            </AnchorTextLink>
            <RoleGate
              roles={[roles.owner, roles.manager, roles.receivablesManager, roles.receivablesContributor]}
              renderGate={({ isAllowed }, children) => (
                <Tooltip
                  content="Starting a call requires higher access. Please speak with your organisation owner to update your permissions."
                  position={Position.TOP}
                >
                  {React.cloneElement(children, { disabled: !isAllowed })}
                </Tooltip>
              )}
            >
              <Button intent="positive" loading={loading} onClick={handleCall}>
                Start call
              </Button>
            </RoleGate>
          </Shelf>
        </Shelf>
      </Shelf>
    </Shelf>
  );
};

export default StartCallBanner;
